/**
 * Zustand store para filtros da auditoria
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AuditSeverity } from '@claro/shared';

export type AuditValidityFilter = 'valid' | 'invalid' | null;

interface AuditFiltersState {
  severities: AuditSeverity[];
  field: string | null;
  regional: string | null;
  search: string;
  alvaraValidity: AuditValidityFilter;
  avcbValidity: AuditValidityFilter;
  page: number;
  limit: number;
  // Actions
  setSeverities: (s: AuditSeverity[]) => void;
  toggleSeverity: (s: AuditSeverity) => void;
  setField: (f: string | null) => void;
  setRegional: (r: string | null) => void;
  setSearch: (q: string) => void;
  setAlvaraValidity: (value: AuditValidityFilter) => void;
  setAvcbValidity: (value: AuditValidityFilter) => void;
  setPage: (p: number) => void;
  resetFilters: () => void;
  getFilterParams: () => Record<string, string>;
}

const DEFAULTS = {
  severities: [] as AuditSeverity[],
  field: null as string | null,
  regional: null as string | null,
  search: '',
  alvaraValidity: null as AuditValidityFilter,
  avcbValidity: null as AuditValidityFilter,
  page: 1,
  limit: 50,
};

export const useAuditFilters = create<AuditFiltersState>()(
  devtools(
    (set, get) => ({
      ...DEFAULTS,
      setSeverities: (severities) => set({ severities, page: 1 }),
      toggleSeverity: (s) => {
        const current = get().severities;
        const next = current.includes(s)
          ? current.filter(v => v !== s)
          : [...current, s];
        set({ severities: next, page: 1 });
      },
      setField: (field) => set({ field, page: 1 }),
      setRegional: (regional) => set({ regional, page: 1 }),
      setSearch: (search) => set({ search, page: 1 }),
      setAlvaraValidity: (alvaraValidity) => set({ alvaraValidity, page: 1 }),
      setAvcbValidity: (avcbValidity) => set({ avcbValidity, page: 1 }),
      setPage: (page) => set({ page }),
      resetFilters: () => set({ ...DEFAULTS }),
      getFilterParams: () => {
        const state = get();
        const params: Record<string, string> = {};
        if (state.severities.length > 0) params.severities = state.severities.join(',');
        if (state.field) params.field = state.field;
        if (state.regional) params.regional = state.regional;
        if (state.search) params.search = state.search;
        if (state.alvaraValidity) params.alvaraValidity = state.alvaraValidity;
        if (state.avcbValidity) params.avcbValidity = state.avcbValidity;
        params.page = String(state.page);
        params.limit = String(state.limit);
        return params;
      },
    }),
    { name: 'audit-filters' }
  )
);
